import React, { useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { createContext, useContext, useState } from 'react'

type ConfirmButtons = {
  label: string
  testId: string
  value: number
  className: string
  operation?: () => Promise<unknown> | void
}

type ConfirmOptions = {
  title: string
  message: string | ReactNode
  hideCancel?: boolean
  confirmLabel?: string
}

export type ConfirmFn = (options: ConfirmOptions, fn: () => void | Promise<unknown>) => Promise<boolean>

export type ChooseFn = (
  config: ConfirmOptions,
  options: ConfirmButtons[],
  fn: (result: number) => void | Promise<unknown>
) => Promise<number | boolean>

export type PromptInputType = 'text' | 'number' | 'email' | 'password'

type PromptOptions<T = string | number> = ConfirmOptions & {
  inputLabel?: string
  placeholder?: string
  defaultValue?: string | number
  type?: PromptInputType // default: 'text'
  min?: number
  max?: number
  step?: number
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode']
  pattern?: string
  validate?: (value: T) => string | null | undefined
  parse?: (raw: string) => T
  submitLabel?: string // default: 'OK'
}

export type PromptFn = <T = string | number>(options: PromptOptions<T>, fn: (result: T) => void | Promise<unknown>) => Promise<T | boolean>

const ConfirmContext = createContext<{
  confirm: ConfirmFn
  choose: ChooseFn
  prompt: PromptFn
} | null>(null)

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider')
  return ctx
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  type StoredPromptOptions = Omit<PromptOptions<unknown>, 'validate' | 'parse'> & {
    validate?: (value: unknown) => string | null | undefined
    parse?: (raw: string) => unknown
  }

  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const [buttons, setButtons] = useState<ConfirmButtons[]>([])

  const resolverRef = useRef<((value: unknown) => void) | null>(null)

  const [promptCfg, setPromptCfg] = useState<StoredPromptOptions | null>(null)
  const [promptValue, setPromptValue] = useState<string>('')
  const [promptError, setPromptError] = useState<string | null>(null)

  const resetAll = () => {
    setOptions(null)
    setButtons([])
    setPromptCfg(null)
    setPromptValue('')
    setPromptError(null)
    resolverRef.current = null
  }

  const handleClose = (result: unknown) => {
    resolverRef.current?.(result)
    resetAll()
  }

  const confirmPromise: (opts: ConfirmOptions) => Promise<boolean> = async (opts) =>
    new Promise((resolve) => {
      setOptions(opts)
      setButtons([])
      resolverRef.current = (v) => resolve(Boolean(v))
    })

  const confirm: ConfirmFn = async (opts, fn) => {
    const ok = await confirmPromise(opts)
    if (ok) await fn()
    return ok
  }

  const choosePromise: (opts: ConfirmOptions, buttons: ConfirmButtons[]) => Promise<number | boolean> = async (opts, buttons) =>
    new Promise((resolve) => {
      setOptions(opts)
      setButtons(buttons)
      resolverRef.current = (v) => resolve(v as number | boolean)
    })

  const choose: ChooseFn = async (opts, buttons, fn) => {
    const choosen = await choosePromise(opts, buttons)
    if (choosen !== false && choosen !== true) {
      await fn(choosen)
    }
    return choosen
  }

  const defaultParse = (type: PromptInputType) => (raw: string) => {
    if (type === 'number') {
      const n = Number(raw)
      return n as unknown
    }
    return raw as unknown
  }

  const doValidate = (cfg: PromptOptions<unknown>, parsed: unknown): string | null => {
    if (cfg.validate) return cfg.validate(parsed) || null
    if (cfg.type === 'number') {
      if (promptValue.trim() === '') return 'Please enter a number'
      if (Number.isNaN(parsed)) return 'Please enter a valid number'
      if (cfg.min !== undefined && (parsed as number) < cfg.min) return `Must be ≥ ${cfg.min}`
      if (cfg.max !== undefined && (parsed as number) > cfg.max) return `Must be ≤ ${cfg.max}`
    }
    return null
  }

  const promptPromise = async <T,>(cfg: PromptOptions<T>): Promise<T | boolean> =>
    new Promise((resolve) => {
      setOptions(cfg)
      setButtons([])
      /* needed since the Type erasure at runtime makes it unsafe to reason here */
      setPromptCfg(cfg as StoredPromptOptions)
      setPromptValue(cfg.defaultValue !== undefined ? String(cfg.defaultValue) : '')
      setPromptError(null)
      resolverRef.current = (v) => resolve(v as T | boolean)
    })

  const prompt: PromptFn = async (cfg, fn) => {
    const val = await promptPromise(cfg)
    if (val !== false && val !== true) {
      await fn(val)
    }
    return val
  }

  const isPrompting = Boolean(promptCfg)
  const inputType: PromptInputType = promptCfg?.type ?? 'text'

  useEffect(() => {
    if (!promptCfg) return
    const parse = (promptCfg.parse as (raw: string) => unknown) ?? defaultParse(inputType)
    const parsed = parse(promptValue)
    setPromptError(doValidate(promptCfg, parsed))
  }, [promptValue, promptCfg, inputType])

  useEffect(() => {
    if (!isPrompting) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClose(false)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        submitPrompt()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isPrompting, promptValue, promptCfg])

  const submitPrompt = () => {
    if (!promptCfg) return
    const parse = (promptCfg.parse as (raw: string) => unknown) ?? defaultParse(inputType)
    const parsed = parse(promptValue)
    const err = doValidate(promptCfg, parsed)
    if (err) {
      setPromptError(err)
      return
    }
    handleClose(parsed)
  }

  const ctx = useMemo(() => ({ confirm, choose, prompt }), [confirm, choose, prompt])

  return (
    <ConfirmContext.Provider value={ctx}>
      {children}
      {options && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-h-[80vh] overflow-y-auto max-w-md">
            <h2 className="text-lg font-semibold mb-2">{options.title}</h2>
            <p className="text-sm text-gray-700 mb-6">{options.message}</p>

            {/* Prompt UI */}
            {isPrompting && (
              <div className="mb-4">
                {promptCfg?.inputLabel && <label className="block text-sm font-medium text-gray-900 mb-1">{promptCfg.inputLabel}</label>}
                <input
                  type={inputType}
                  inputMode={promptCfg?.inputMode}
                  pattern={promptCfg?.pattern}
                  min={promptCfg?.min}
                  max={promptCfg?.max}
                  step={promptCfg?.step}
                  placeholder={promptCfg?.placeholder}
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-3.5 py-2 text-[15px] outline-hidden focus:ring-4 focus:ring-black/10 focus:border-black"
                  autoFocus
                />
                {promptError && (
                  <p className="mt-1 text-xs text-red-600" role="alert">
                    {promptError}
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              {!options.hideCancel && (
                <button className="btn" onClick={() => handleClose(false)}>
                  Cancel
                </button>
              )}

              {/* Choose buttons */}
              {buttons.length > 0 && (
                <>
                  {buttons.map((o, i) => (
                    <button
                      key={i}
                      data-testid={o.testId}
                      className={`btn ${o.className}`}
                      onClick={async () => {
                        if (o.operation) {
                          await o.operation()
                        }
                        handleClose(o.value)
                      }}
                    >
                      {o.label}
                    </button>
                  ))}
                </>
              )}

              {/* Confirm / Prompt submit */}
              {buttons.length === 0 && !isPrompting && (
                <button data-test-id="confirm-btn" className="btn btn-primary" onClick={() => handleClose(true)}>
                  {options.confirmLabel ?? 'Confirm'}
                </button>
              )}

              {isPrompting && (
                <button data-test-id="prompt-submit" className="btn btn-primary" onClick={submitPrompt} disabled={Boolean(promptError)}>
                  {promptCfg?.submitLabel ?? 'OK'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}
